import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    parserFunction: (...args: any[]) => {};
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onComplete?: Function;
    onError?: Function;
    variables?: any;
    appConfig?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchDrilldownData({
    url,
    parserFunction,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    appConfig = {},
    queryConfig = { enabled: true },
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data && queryConfig.enabled) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess, queryConfig.enabled]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData = parserFunction(payload, appConfig);
            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchDrilldownData };
